import { useState, useCallback } from 'react'

/**
 * Custom hook to manage shift-click selection in a list using an object to track selected items.
 * @template T The type of the items in the list, which should include an `id` property.
 * @returns An object containing the selected items and the method to handle row click events.
 */
export const useShiftSelectable = <T extends { id: string | number }>() => {
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [lastSelectedItemId, setLastSelectedItemId] = useState<string | number | null>(null)
  const numberOfSelected = Object.values(selected).filter(val => !!val).length

  const handleRowClick = useCallback(
    (clickedItem: T, allItems: T[], isShiftClick: boolean) => {
      const updateSelection = (itemId: string | number, isSelected: boolean) => {
        setSelected(prev => ({
          ...prev,
          [itemId]: isSelected,
        }))
      }

      const toggleItemSelection = (itemId: string | number) => {
        setSelected(prev => ({
          ...prev,
          [itemId]: !prev[itemId],
        }))
      }

      if (!isShiftClick || lastSelectedItemId === null) {
        toggleItemSelection(clickedItem.id)
        setLastSelectedItemId(clickedItem.id)
        return
      }

      const startIndex = allItems.findIndex(item => item.id === lastSelectedItemId)
      const endIndex = allItems.findIndex(item => item.id === clickedItem.id)
      const rangeStart = Math.min(startIndex, endIndex)
      const rangeEnd = Math.max(startIndex, endIndex)

      allItems.slice(rangeStart, rangeEnd + 1).forEach(item => {
        updateSelection(item.id, true)
      })

      // Optionally, update the last selected item ID if you want to continue selecting ranges based on the last click
      // setLastSelectedItemId(clickedItem.id);
    },
    [lastSelectedItemId],
  )

  return { selected, setSelected, handleRowClick, numberOfSelected }
}
